import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Box,
  Color,
  colors,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading2,
  Overflow,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { Background } from '../AboveTheFoldSection/components/Background/Background'
import { DiscountBox } from '../AboveTheFoldSection/components/DiscountBanner/config'
import { Sections, trackCTAClick, TrackingActions } from '../AboveTheFoldSection/tracking'
import { CWCDiscountPills } from '../CWCDiscountPills/CWCDiscountPills'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import kingPrawnTeriyakiDonburi from '../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi.png'
import kingPrawnTeriyakiDonburiSm from '../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi-sm.png'
import kingPrawnTeriyakiDonburiWebp from '../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi.webp'
import kingPrawnTeriyakiDonburiWebpSm from '../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi-sm.webp'
import tandooriChicken from '../../assets/CWCFirstBoxSectionAssets/plates/tandoori-chicken.png'
import tandooriChickenSm from '../../assets/CWCFirstBoxSectionAssets/plates/tandoori-chicken-sm.png'
import tandooriChickenWebp from '../../assets/CWCFirstBoxSectionAssets/plates/tandoori-chicken.webp'
import tandooriChickenWebpSm from '../../assets/CWCFirstBoxSectionAssets/plates/tandoori-chicken-sm.webp'
import bologneseRagu from '../../assets/CWCFirstBoxSectionAssets/plates/bolognese-ragu.png'
import bologneseRaguSm from '../../assets/CWCFirstBoxSectionAssets/plates/bolognese-ragu-sm.png'
import bologneseRaguWebp from '../../assets/CWCFirstBoxSectionAssets/plates/bolognese-ragu.webp'
import bologneseRaguWebpSm from '../../assets/CWCFirstBoxSectionAssets/plates/bolognese-ragu-sm.webp'
import salmon from '../../assets/CWCFirstBoxSectionAssets/plates/salmon.png'
import salmonSm from '../../assets/CWCFirstBoxSectionAssets/plates/salmon-sm.png'
import salmonWebp from '../../assets/CWCFirstBoxSectionAssets/plates/salmon.webp'
import salmonWebpSm from '../../assets/CWCFirstBoxSectionAssets/plates/salmon-sm.webp'

export type CWCFirstBoxSectionProps = {
  // Same DiscountBox[] source the Hero receives — the common caller (B20-1210) passes the identical
  // selection to both so the tiers can never drift. No hardcoded 50/40/20 here.
  discountBoxes: DiscountBox[]
  onCTAClick: () => void
}

// Sign-up destination for the "Get started" CTA. Fixed for this section (not caller-configurable).
const CTA_HREF = '/signup/box-size'

// 4-value responsive arrays map to [base(<640), 640+, 768+ (tablet), 1024+ (desktop)] per the Citrus
// breakpoints — matching CWCHero. Tablet (768–1023) is a distinct band from desktop (≥1024).

// The pattern background sits in an absolute, full-size layer behind the content (mirrors how
// AboveTheFoldSection composes the same Background component). display:flex so the Background's
// Container child (which sets only width) stretches to the full section height — otherwise it
// collapses to zero height and the pattern never paints.
const BackgroundLayer = withCitrusTheme(Box, () => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
}))

// Section heading ("Your first box, / half off"). ExtraBold, centred; 36/40 on mobile+tablet stepping
// to 40/44 on desktop — both are on the Citrus font scale (size 13 = 36/40, size 14 = 40/44), so the
// size is a responsive `size` token passed at the call site rather than a raw responsiveStyle. Only the
// bespoke tracking (-1.82px → normal) stays custom, media-wrapped so it wins over Citrus's own default.
const Heading = withCitrusTheme(Heading2, (theme) => [
  { fontWeight: 800 } as CSSObject,
  theme.responsiveStyle(
    ['-1.82px', '-1.82px', '-1.82px', 'normal'],
    ['letterSpacing'],
  ) as CSSObject,
])

// Supporting copy — 16/24 = Citrus font scale `size={4}`, rendered inline as a plain <Text> below.

// The "Get started" CTA uses the shared CWCLinkButton (a real <a href> — keyboard-accessible, and the
// keyboard semantics incl. Space activation live in that component so every consumer benefits).

// Reassurance line — 14/20 = Citrus font scale `size={2}` (passed at the call site); centred, two rows.
// Only the White colour is encapsulated here, shared across both rows.
const ReassuranceText = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.White),
}))

// Content column. Vertical padding is 128px on desktop — above the SpaceScale ceiling (20 = 80px) —
// so it goes via responsiveStyle rather than a paddingV scale prop (mobile/tablet tuned in the
// fidelity loop). Horizontal padding + gaps stay as scale props on the Box.
const ContentColumn = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(
    ['72px', '72px', '56px', '128px'],
    ['paddingTop', 'paddingBottom'],
  ) as CSSObject,
])

// Floating plate cut-outs — four distinct dish photos framing the section. The committed PNGs are
// 300x300 (dish centred on transparent padding, matching Figma's 300x300 "Plate cut-outs" frame), so
// they render in square boxes — 180 (mobile) / 200 (tablet) / 300 (desktop) per Citrus band — and the
// Figma frame coordinates apply uniformly to every plate. alt="" — decorative framing imagery.
// ITERATION 1 (2026-07-19): placeholder assets, no WebP/srcset/@2x yet — designer's responsive assets
// land in a follow-up (B20-1184 iteration 2).
const PLATE_SIZE: [string, string, string, string] = ['180px', '180px', '200px', '300px']

// Display width per breakpoint, so the browser fetches the smallest sufficient plate variant
// (sm=360w for mobile/tablet, base=500w for desktop) rather than one desktop-sized asset everywhere.
const PLATE_SIZES = '(min-width: 1024px) 300px, (min-width: 768px) 200px, 180px'

// Per-plate corner offsets [mobile, 640, tablet, desktop] read from the Figma frames (mobile
// 724:4044 / tablet 724:4014); right/bottom values converted from Figma's left/top so each plate
// pins to one horizontal + one vertical edge and stays put as the viewport width varies. Desktop
// keeps balanced -40 corners (already signed off). Top-left plate is rotated 90° per the design.
type PlateEdges = Partial<
  Record<'top' | 'bottom' | 'left' | 'right', [string, string, string, string]>
>

const makePlate = (edges: PlateEdges, rotate = false) =>
  withCitrusTheme('img', (theme) => [
    {
      position: 'absolute',
      objectFit: 'contain',
      pointerEvents: 'none',
      ...(rotate ? { transform: 'rotate(90deg)' } : {}),
    } as CSSObject,
    // Square boxes matching Figma's 300/200/180 plate frames — the assets are 300x300 (dish centred
    // with transparent padding), so the Figma coordinates apply uniformly to every plate.
    theme.responsiveStyle(PLATE_SIZE, ['width']) as CSSObject,
    theme.responsiveStyle(PLATE_SIZE, ['height']) as CSSObject,
    ...(Object.keys(edges) as Array<keyof PlateEdges>).map(
      (prop) => theme.responsiveStyle(edges[prop]!, [prop]) as CSSObject,
    ),
  ])

// Mobile = exact Figma frame-724:4044 coords (possible now the backgrounds are transparent — only the
// round dish overlaps, curving clear of the centred text). Tablet = frame-724:4014 tuning. Desktop =
// frame-724:3984 coords anchored to the content centre via calc(50% ± offset) — the desktop plates
// flank the centred content rather than sitting in the frame corners, so centre-relative anchoring
// keeps them correct as the viewport widens. TR/BR switch from right- to left-anchoring on desktop.
// Top plates are centre-anchored on mobile too (calc(50% ± offset)) so they slide with the centred
// heading as the frame narrows — keeping constant clearance from the text at 320→430 while matching
// the Figma 390 position. (Edge-anchoring here would let the leftward-sliding heading collide with a
// fixed-offset plate on small phones.) Tablet stays edge-anchored; desktop uses its own centre offset.
const PlateTopLeft = makePlate(
  {
    top: ['-66px', '-66px', '-43px', '-3px'],
    left: ['calc(50% - 261px)', 'calc(50% - 261px)', '0px', 'calc(50% - 462px)'],
  },
  true,
)
const PlateTopRight = makePlate({
  top: ['-8px', '-8px', '1px', '5px'],
  right: ['auto', 'auto', '0px', 'auto'],
  left: ['calc(50% + 120px)', 'calc(50% + 120px)', 'auto', 'calc(50% + 197px)'],
})
const PlateBottomLeft = makePlate({
  bottom: ['-82px', '-82px', '-70px', '-120px'],
  left: ['-91px', '-91px', '40px', 'calc(50% - 383px)'],
})
const PlateBottomRight = makePlate({
  // Mobile bottom pushed to -110 (vs Figma -55): the designer's salmon is a fuller plate than the
  // placeholder, so it needs to sit lower to clear the reassurance line on the tight mobile layout.
  bottom: ['-110px', '-110px', '-11px', '-45px'],
  right: ['-80px', '-80px', '-42px', 'auto'],
  left: ['auto', 'auto', 'auto', 'calc(50% + 330px)'],
})

export function CWCFirstBoxSection({ discountBoxes, onCTAClick }: CWCFirstBoxSectionProps) {
  const handleAndTrackCTAClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent the anchor's native navigation so the JS sign-up handler (onCTAClick) owns routing — in
    // the live homepage it applies promos before the SPA redirect, which the browser would otherwise
    // race by following href synchronously. href stays a no-JS fallback (this only runs when JS is on).
    event.preventDefault()
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, Sections.HALF_OFF)
  }

  return (
    <Box
      data-testid="first-box-section"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      width="100%"
      bg={Color.ColdGrey_800}
    >
      <BackgroundLayer>
        <Background />
      </BackgroundLayer>
      <ContentColumn
        position={DisplayPosition.Relative}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
        gap={8}
        paddingH={6}
      >
        {/* Heading + copy + tiers */}
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
          gap={[4, 4, 2, 4]}
          width="100%"
        >
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
            gap={[2, 2, 3, 3]}
            width="100%"
          >
            <Heading
              color={Color.White}
              fontFamily={FontFamily.ExtraBold}
              textAlign={TextAlign.Center}
              size={[13, 13, 13, 14]}
            >
              Your first box,
              <br />
              <span style={{ color: colors[Color.Primary_600] }}>half off</span>
            </Heading>
            <Text color={Color.White} textAlign={TextAlign.Center} size={4}>
              Plus more discounts on your next seven.
            </Text>
          </Box>
          <CWCDiscountPills discountBoxes={discountBoxes} width="318px" />
        </Box>

        {/* CTA + reassurance */}
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
          gap={[3, 3, 3, 4]}
          width={['100%', '343px', '343px', '343px']}
        >
          <CWCLinkButton href={CTA_HREF} onClick={handleAndTrackCTAClick}>
            Get started
          </CWCLinkButton>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
          >
            <ReassuranceText textAlign={TextAlign.Center} size={2}>
              <span style={{ fontWeight: FontWeight.Bold }}>Free next-day delivery</span> on your
              1st box<span style={{ fontSize: '9px', verticalAlign: 'super' }}>†</span>
            </ReassuranceText>
            <ReassuranceText textAlign={TextAlign.Center} size={2}>
              <span style={{ fontWeight: FontWeight.Bold }}>No lock in:</span> Skip or cancel
              anytime
            </ReassuranceText>
          </Box>
        </Box>
      </ContentColumn>
      {/* Each plate: WebP source with width-descriptor srcset (sm 360w / base 500w) + sizes, so the
          browser fetches only the viewport-appropriate variant; transparent-PNG <img> is the fallback.
          The styled <img> carries positioning/size; display:contents keeps <picture> layout-free. */}
      <picture style={{ display: 'contents' }}>
        <source
          type="image/webp"
          srcSet={`${kingPrawnTeriyakiDonburiWebpSm} 360w, ${kingPrawnTeriyakiDonburiWebp} 500w`}
          sizes={PLATE_SIZES}
        />
        <PlateTopLeft
          src={kingPrawnTeriyakiDonburi}
          srcSet={`${kingPrawnTeriyakiDonburiSm} 360w, ${kingPrawnTeriyakiDonburi} 500w`}
          sizes={PLATE_SIZES}
          alt=""
          data-testid="plate"
        />
      </picture>
      <picture style={{ display: 'contents' }}>
        <source
          type="image/webp"
          srcSet={`${tandooriChickenWebpSm} 360w, ${tandooriChickenWebp} 500w`}
          sizes={PLATE_SIZES}
        />
        <PlateTopRight
          src={tandooriChicken}
          srcSet={`${tandooriChickenSm} 360w, ${tandooriChicken} 500w`}
          sizes={PLATE_SIZES}
          alt=""
          data-testid="plate"
        />
      </picture>
      <picture style={{ display: 'contents' }}>
        <source
          type="image/webp"
          srcSet={`${bologneseRaguWebpSm} 360w, ${bologneseRaguWebp} 500w`}
          sizes={PLATE_SIZES}
        />
        <PlateBottomLeft
          src={bologneseRagu}
          srcSet={`${bologneseRaguSm} 360w, ${bologneseRagu} 500w`}
          sizes={PLATE_SIZES}
          alt=""
          data-testid="plate"
        />
      </picture>
      <picture style={{ display: 'contents' }}>
        <source
          type="image/webp"
          srcSet={`${salmonWebpSm} 360w, ${salmonWebp} 500w`}
          sizes={PLATE_SIZES}
        />
        <PlateBottomRight
          src={salmon}
          srcSet={`${salmonSm} 360w, ${salmon} 500w`}
          sizes={PLATE_SIZES}
          alt=""
          data-testid="plate"
        />
      </picture>
    </Box>
  )
}
